import { useLoaderData } from 'react-router'
import { prisma } from '#app/utils/db.server.ts'
import { formatPlanFeatures, formatPlanPrice } from '#app/utils/plan-format.ts'
import { Demo } from './+sections/demo.tsx'
import { Features } from './+sections/features.tsx'
import { FinalCTA } from './+sections/final-cta.tsx'
import { Hero } from './+sections/hero.tsx'
import { Pricing, type PricingPlan } from './+sections/pricing.tsx'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Vigilio — Voir clair dans le bruit médiatique' },
	{
		name: 'description',
		content:
			"La veille médiatique québécoise, faite pour les équipes qui n'ont pas le temps de tout lire. Agrégez vos sources, créez des alertes précises, recevez l'essentiel par courriel.",
	},
]

const PLAN_META: Array<
	Omit<PricingPlan, 'price' | 'features'> & {
		support: string
		extras?: string[]
	}
> = [
		{
			key: 'PRO',
			name: 'Pro',
			description: 'Pour les équipes de communication et OSBL.',
			cta: 'S\'inscrire',
			href: '/signup',
			featured: false,
			support: 'Support courriel',
		},
		{
			key: 'BUSINESS',
			name: 'Business',
			description: 'Pour les organisations qui veillent à grande échelle.',
			cta: 'S\'inscrire',
			href: '/signup',
			featured: true,
			support: 'Support prioritaire (courriel + chat)',
		},
		{
			key: 'ENTERPRISE',
			name: 'Enterprise',
			description: 'Secteur public, ordres professionnels, grandes équipes.',
			cta: 'Nous contacter',
			href: 'mailto:bonjour@vigilio.ca?subject=Demande%20Enterprise',
			featured: false,
			support: 'Support dédié avec gestionnaire de compte',
			extras: ['Options supplémentaires sur demande'],
		},
	]

export async function loader(_: Route.LoaderArgs) {
	const planLimits = await prisma.planLimit.findMany({
		where: { planType: { in: ['PRO', 'BUSINESS', 'ENTERPRISE'] } },
	})
	const byType = Object.fromEntries(planLimits.map((p) => [p.planType, p]))
	return { planLimits: byType }
}

export default function Index() {
	const { planLimits } = useLoaderData<typeof loader>()

	const plans: PricingPlan[] = PLAN_META.flatMap((meta) => {
		const limit = planLimits[meta.key]
		if (!limit) return []
		const { support, extras, ...planMeta } = meta
		return [
			{
				...planMeta,
				price: formatPlanPrice(limit),
				features: [
					...formatPlanFeatures(limit),
					support,
					...(extras ?? []),
				],
			},
		]
	})

	return (
		<>
			<Hero />
			<Features />
			<Demo />
			<Pricing plans={plans} />
			<FinalCTA />
		</>
	)
}
